import React from "react";
import { MapPin, Sailboat, Mountain } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../hooks/useResponsive";
import { useContent } from "../cms/ContentContext";
import InteractiveCard from "./InteractiveCard";
import { colors } from "../styles/colors";

const OwnerSection = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();
  return (
    <section data-cms-section="ownerSection" data-cms-page="home" className={isMobile ? 'py-12' : 'py-20'} style={{ background: colors.gradients.heroSolid }}>
      <div className={`mx-auto ${isMobile ? 'max-w-sm px-4' : 'max-w-7xl px-4 sm:px-6 lg:px-8'
        }`}>
        <div className={`text-center ${isMobile ? 'mb-8' : 'mb-16'
          }`}>
          <h2 className={`font-bold text-white mb-6 ${isMobile ? 'text-3xl' : 'text-4xl md:text-5xl'
            }`}>
            {t('ownerSection.title1')}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent"> {t('ownerSection.title2')}</span>
          </h2>
          <p className={`text-gray-300 mx-auto ${isMobile ? 'text-lg max-w-md' : 'text-xl max-w-3xl'
            }`}>
            {t('ownerSection.intro')}
          </p>
        </div>

        {/* Main Content */}
        <div className={`grid items-center ${isMobile ? 'grid-cols-1 gap-8 mb-8' : 'grid-cols-1 lg:grid-cols-2 gap-16 mb-16'
          }`}>
          {/* Image */}
          <div className={isMobile ? 'order-1' : 'order-2 lg:order-1'}>
            <div className="relative">
              <img
                src={getMedia('ownerSection.ownerImageUrl', '/images/owners.webp')}
                alt="Gustav and Julia - Cold Experience Owners"
                className={`w-full object-cover rounded-2xl shadow-2xl ${isMobile ? 'h-64' : 'h-96'
                  }`}
                loading={isMobile ? "lazy" : "eager"}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl"></div>
            </div>
          </div>

          {/* Content */}
          <div className={isMobile ? 'order-2' : 'order-1 lg:order-2'}>
            <h3 className={`font-bold text-white mb-6 ${isMobile ? 'text-2xl text-center' : 'text-3xl'
              }`}>{t('ownerSection.names')}</h3>
            <p className={`text-gray-300 mb-6 leading-relaxed ${isMobile ? 'text-base text-center' : 'text-lg'
              }`}>
              {t('ownerSection.bio')}
            </p>

            <div className={`grid gap-6 mb-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
              }`}>
              <InteractiveCard
                variant="tilt"
                className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl"
              >
                <div className={isMobile ? 'p-4' : 'p-6'}>
                  <div className={`inline-flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 mb-4 ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                    }`}>
                    <Mountain className={`text-white/90 ${isMobile ? 'h-5 w-5' : 'h-6 w-6'
                      }`} />
                  </div>
                  <h4 className={`font-semibold text-white mb-2 ${isMobile ? 'text-sm' : ''
                    }`}>{t('ownerSection.gustavTitle')}</h4>
                  <p className={`text-gray-100 ${isMobile ? 'text-xs' : 'text-sm'
                    }`}>{t('ownerSection.gustavBio')}</p>
                </div>
              </InteractiveCard>

              <InteractiveCard
                variant="tilt"
                className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl"
              >
                <div className={isMobile ? 'p-4' : 'p-6'}>
                  <div className={`inline-flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 mb-4 ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                    }`}>
                    <Sailboat className={`text-white/90 ${isMobile ? 'h-5 w-5' : 'h-6 w-6'
                      }`} />
                  </div>
                  <h4 className={`font-semibold text-white mb-2 ${isMobile ? 'text-sm' : ''
                    }`}>{t('ownerSection.juliaTitle')}</h4>
                  <p className={`text-gray-100 ${isMobile ? 'text-xs' : 'text-sm'
                    }`}>{t('ownerSection.juliaBio')}</p>
                </div>
              </InteractiveCard>
            </div>

            <InteractiveCard
              variant="tilt"
              className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl"
            >
              <div className={isMobile ? 'p-4' : 'p-6'}>
                <div className={`inline-flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 mb-4 ${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                  }`}>
                  <MapPin className={`text-white/90 ${isMobile ? 'h-5 w-5' : 'h-6 w-6'
                    }`} />
                </div>
                <p className={`text-white font-medium mb-2 ${isMobile ? 'text-sm' : ''
                  }`}>{t('ownerSection.lifestyleTitle')}</p>
                <p className="text-gray-100 text-sm">{t('ownerSection.lifestyleQuote')}</p>
              </div>
            </InteractiveCard>
          </div>
        </div>

        {/* Additional Info */}
        <InteractiveCard
          variant="tilt"
          className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl"
        >
          <div className="p-8 text-center">
            <h4 className="text-2xl font-bold text-white mb-6">
              {t('ownerSection.whyTitle')}
            </h4>
            <p className="text-lg text-gray-100 max-w-4xl mx-auto leading-relaxed mb-6">
              {t('ownerSection.whyText')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl font-bold mb-2" style={{ color: colors.accent.hover }}>{t('ownerSection.localExpertiseTitle')}</div>
                <p style={{ color: colors.text.secondary }}>{t('ownerSection.localExpertiseText')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-2" style={{ color: colors.accent.hover }}>{t('ownerSection.familyBusinessTitle')}</div>
                <p style={{ color: colors.text.secondary }}>{t('ownerSection.familyBusinessText')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-2" style={{ color: colors.accent.hover }}>{t('ownerSection.authenticExperienceTitle')}</div>
                <p style={{ color: colors.text.secondary }}>{t('ownerSection.authenticExperienceText')}</p>
              </div>
            </div>
          </div>
        </InteractiveCard>
      </div>
    </section>
  );
};

export default OwnerSection;